import { useState } from 'react';
import { Box } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { IconCheck, IconPencil, IconTrash, IconX } from '@tabler/icons-react';

import TextField from '../TextField';
import Tooltip from '../Tooltip';
import Typography from '../Typography/Typography';
import { TextEditorProps } from './types';

const TextEditor = ({
  cancelButtonAriaLabel,
  defaultValue,
  deleteButtonAriaLabel,
  disabledDeleteButtonTooltipContent,
  editButtonAriaLabel,
  hideDeleteButton,
  isDeleteButtonDisabled = false,
  onDelete,
  onSave,
  placeholder,
  saveButtonAriaLabel,
  textInputAriaLabel,
}: TextEditorProps) => {
  const theme = useTheme();

  const [isTextInputVisible, setIsTextInputVisible] = useState(false);
  const [textInputValue, setTextInputValue] = useState(defaultValue);

  const saveTextInputValue = () => {
    const isValueEmpty = !textInputValue.trim();
    if (isValueEmpty) {
      setTextInputValue(defaultValue);
    } else {
      onSave(textInputValue);
    }
    setIsTextInputVisible(false);
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: '12px',
        height: '40px',
        width: '100%',
      }}
    >
      {isTextInputVisible ? (
        <Box flex='1'>
          <TextField
            shouldErrorMessageTakeUpSpace={false}
            aria-label={textInputAriaLabel || 'Enter value'}
            autoFocus
            onBlur={saveTextInputValue}
            onChange={e => setTextInputValue(e.target.value)}
            placeholder={placeholder || 'Enter value'}
            value={textInputValue}
          />
        </Box>
      ) : (
        <Box
          flex='1'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
        >
          <Typography variant='font14'>{defaultValue}</Typography>
        </Box>
      )}

      <Box display='flex' gap='5px'>
        {isTextInputVisible ? (
          <>
            <Button
              onMouseDown={e => {
                e.stopPropagation();
                saveTextInputValue();
              }}
            >
              <IconCheck
                aria-label={saveButtonAriaLabel || 'Save'}
                color={theme.palette.colors.grey[500]}
                height='20px'
                width='20px'
              />
            </Button>
            <Button
              onMouseDown={e => {
                e.stopPropagation();
                setIsTextInputVisible(false);
              }}
            >
              <IconX
                aria-label={cancelButtonAriaLabel || 'Cancel'}
                color={theme.palette.colors.grey[500]}
                height='20px'
                width='20px'
              />
            </Button>
          </>
        ) : (
          <>
            <Button
              aria-label={editButtonAriaLabel || 'Edit text'}
              onClick={e => {
                e.stopPropagation();
                setTextInputValue(defaultValue);
                setIsTextInputVisible(true);
              }}
            >
              <IconPencil
                color={theme.palette.colors.grey[500]}
                height='20px'
                width='20px'
              />
            </Button>
            {!hideDeleteButton && (
              <Tooltip
                tooltipContent={
                  isDeleteButtonDisabled
                    ? disabledDeleteButtonTooltipContent
                    : ''
                }
              >
                <Button
                  disabled={isDeleteButtonDisabled}
                  aria-label={deleteButtonAriaLabel || 'Delete'}
                  onClick={e => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <IconTrash
                    color={theme.palette.colors.grey[500]}
                    height='20px'
                    width='20px'
                  />
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TextEditor;

const Button = styled('button')`
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  &:hover:not(:disabled) {
    cursor: pointer;
    [stroke] {
      stroke: ${({ theme }) => theme.palette.colors.purple[500]};
    }
  }
`;
